import {
  CircularProgress,
  IconButton,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material';
import { forwardRef, useMemo } from 'react';
import { isNullish } from 'remeda';
import { DynamicIcon, Tooltip } from 'src/components';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { Macro } from 'src/graphql';

const EMPTY_PREVIEW = 'No description provided.';

interface MacroListItemProps {
  entryId: string;
  macro: Macro;
  isFavorite: boolean;
  canToggleFavorite: boolean;
  isHighlighted: boolean;
  isManageMode: boolean;
  canEditPlatformMacros: boolean;
  isDeleting: boolean;
  canManageMacros: boolean;
  currentUserId: string | undefined;
  onToggleFavorite: () => void;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export const MacroListItem = forwardRef<HTMLDivElement, MacroListItemProps>(
  (
    {
      entryId,
      macro,
      isFavorite,
      canToggleFavorite,
      isHighlighted,
      isManageMode,
      canEditPlatformMacros,
      isDeleting,
      canManageMacros,
      currentUserId,
      onToggleFavorite,
      onSelect,
      onEdit,
      onDelete,
    },
    ref,
  ) => {
    const isPlatformMacro =
      isNullish(macro.partnerId) || macro.partnerId === PLATFORM_PARTNER_ID;
    const isOwner = Boolean(currentUserId && macro.createdBy === currentUserId);
    const canEdit = canEditPlatformMacros || !isPlatformMacro || isOwner;

    const previewText = useMemo(() => {
      if (macro.description?.trim()) {
        return macro.description.trim();
      }

      return EMPTY_PREVIEW;
    }, [macro.description]);

    return (
      <ListItemButton
        id={entryId}
        ref={ref}
        selected={isHighlighted}
        onClick={() => {
          if (!isManageMode) {
            onSelect();
          }
        }}
        sx={{
          paddingLeft: 6,
          paddingRight: 3,
          paddingY: 1.25,
          alignItems: 'flex-start',
          opacity: isManageMode && !canEdit ? 0.6 : 1,
        }}
      >
        <Stack direction="row" gap={1.5} flex={1} alignItems="flex-start">
          <DynamicIcon name="chat_bubble_outline" weight={400} />
          <Stack flex={1} minWidth={0} gap={0.5}>
            <Typography
              variant="subtitle2"
              fontWeight={500}
              sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
            >
              {macro.name}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
            >
              {previewText}
            </Typography>
          </Stack>
          <Tooltip title={isFavorite ? 'Unfavorite macro' : 'Favorite macro'}>
            <span>
              <IconButton
                aria-label={isFavorite ? 'Unfavorite macro' : 'Favorite macro'}
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleFavorite();
                }}
                disabled={!canToggleFavorite}
              >
                <DynamicIcon
                  name="star"
                  filled={isFavorite}
                  sx={{
                    color: isFavorite ? 'warning.main' : 'text.secondary',
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
          {isManageMode && canManageMacros && canEdit ? (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Tooltip title="Edit macro">
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit();
                  }}
                >
                  <DynamicIcon name="edit" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete macro">
                <span>
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      onDelete();
                    }}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <CircularProgress size={18} />
                    ) : (
                      <DynamicIcon name="delete" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          ) : null}
        </Stack>
      </ListItemButton>
    );
  },
);

MacroListItem.displayName = 'MacroListItem';
